// ============================================================
// sanitizeEmailHtml.ts
// SECURITY — client-side sanitisation of user-supplied email HTML (DOMPurify).
//
// Used by EmailSignaturePanel when a user PASTES or UPLOADS raw HTML for their
// signature: we sanitise before previewing and before saving, so what the user
// sees and stores is already clean. The backend (htmlSanitizer.ts) re-sanitises
// on save AND on send — this client pass is convenience + preview, NOT the
// authoritative gate.
//
// DOMPurify strips <script>/<style>/<iframe>, all on* handlers and
// javascript:/vbscript: URLs by default, while keeping normal formatting
// (headings, spans, tables, inline styles, links). It also permits
// data:image/* on <img> by default (DOMPurify's built-in DATA_URI_TAGS), which
// we need so a pasted inline image survives to become a cid: attachment on send.
// ============================================================
import DOMPurify from 'dompurify'

// Keep target/rel so signature links can open in a new tab, and force
// noopener/noreferrer on any link that has target=_blank (reverse-tabnabbing).
if (typeof window !== 'undefined') {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer nofollow')
    }
  })
}

export function sanitizeEmailHtml(html: string): string {
  if (!html) return ''
  return DOMPurify.sanitize(html, {
    // Allow links to open in a new tab (target survives; rel added by the hook).
    ADD_ATTR: ['target'],
    // Belt-and-braces: never allow these even if a future default changes.
    // <style> is stripped (global CSS is the classic "highlights everything"
    // leak). class/id are stripped too so a mail-client export's utility classes
    // can't collide with / restyle the app's own elements.
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'noscript'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'class', 'id'],
  })
}
